import { Component, h, Host, Listen, State } from '@stencil/core';
import { Facets } from '~model/solr/facets';
import { Search as SearchInterface } from '~model/solr/search';
import { Result } from '~model/solr/result';
import { get, normalizeUrl } from '~service/ajax-service';
import { translate } from '~service/translation-service';

@Component({
  tag: 'uvex-search',
  styleUrl: 'search.scss',
})
export class Search {
  @State() result: Result;
  @State() facets: Facets;
  @State() search: SearchInterface;
  @State() loading = false;

  async componentWillLoad() {
    await this.load();
  }

  @Listen('popstate', { target: 'window' })
  async load() {
    this.loading = true;
    try {
      const currentUrl = new URL(normalizeUrl(window.location.href));
      const { facets, result, search } = await get(currentUrl.toString());
      this.facets = facets;
      this.result = result.length === 0 ? null : result;
      this.search = search;
      this.preloadResults();
    } catch (e) {
    } finally {
      this.loading = false;
    }
  }

  select(url: string) {
    window.history.replaceState({}, '', url.toString());
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  async loadNext() {
    this.loading = true;

    try {
      const currentUrl = new URL(normalizeUrl(this.search.links.next));
      const { result, search } = await get(currentUrl.toString());
      this.search = search;
      this.result = Object.assign({}, this.result, { items: [...this.result.items, ...result.items] });
      this.preloadResults();
    } catch (e) {
    } finally {
      this.loading = false;
    }
  }

  private async preloadResults() {
    const currentUrl = new URL(normalizeUrl(this.search.links.next));
    await get(currentUrl.toString());
  }

  private renderEmptySearch() {
    if (this.search.q !== '') {
      return;
    }
    return (
      <div class="container search-empty-results">
        <p>{translate('emptySearch')}</p>
      </div>
    );
  }

  private renderNoResults() {
    if (this.result?.totalResults !== 0) {
      return;
    }
    return (
      <div class="container search-no-results">
        <p class="lead">{translate('noResults', this.search.q)}</p>
      </div>
    );
  }

  private renderResults() {
    if (this.result === null || this.result.totalResults === 0) {
      return;
    }

    return (
      <div class="bg-faded search-results-container">
        <div class="container">
          {this.renderResultItems()}
          {this.renderLoadMore()}
        </div>
      </div>
    );
  }

  private renderFacets() {
    if (!this.facets.hasOwnProperty('type')) {
      return;
    }
    const facet = this.facets['type'];
    return (
      <div class="container">
        <label>{translate('filter.headline')}</label>
        <div class="search-facet clearfix">
          {facet.options.map(option => (
            <div class="radio">
              <input type="radio" checked={option.selected} />
              <label onClick={_ => this.select(option.links.self)}>{option.label}</label>
            </div>
          ))}
        </div>
        <p class="lead search-results-number">{translate('totalResults', this.search.q, this.result.totalResults)}</p>
      </div>
    );
  }

  private renderResultItems() {
    return <div class="search-results-items">{this.result.items.map(item => this.renderResultItem(item))}</div>;
  }

  private renderResultItem(item) {
    const imageStyles = {};
    if (item.image) {
      imageStyles['backgroundImage'] = `url('${item.image}')`;
    }

    return (
      <div class="search-result-item clearfix">
        <div class="search-result-image" style={imageStyles} />
        <div class="search-result-content">
          <h3>
            <a class="link-full" href={item.url} innerHTML={item.title} />
          </h3>
          <p>
            <span innerHTML={item.content} />
            <a class="search-result-more nowrap" href={item.url}>
              {translate('more')}
            </a>
          </p>
        </div>
      </div>
    );
  }

  private renderLoadMore() {
    const moreCount = this.result.totalResults - (this.result.items || []).length;
    if (moreCount <= 0) {
      return;
    }
    if (this.search.links?.next) {
      return (
        <div class="search-results-load-more text-xs-center">
          <a class="btn btn-link btn-lg icon-left" onClick={_ => this.loadNext()} ga-on="click" ga-event-category="search" ga-event-label="load-more" role="button">
            <svg width="32" height="32">
              <use href="#fa-plus-circle"></use>
            </svg>
            <span>
              {translate('loadMore')} ({moreCount})
            </span>
          </a>
        </div>
      );
    }
  }

  render() {
    return (
      <Host>
        <div class="container container-search-box">
          <uvex-search-box
            search-url={this.search?.links?.reset}
            search-parameter="tx_solr[q]"
            suggest-url={this.search?.links?.suggest}
            suggest-parameter="tx_solr[queryString]"
          />
          {this.renderEmptySearch()}
          {this.renderNoResults()}
          {this.renderFacets()}
        </div>
        {this.renderResults()}
      </Host>
    );
  }
}
